import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { vehicleService } from "../services/api";
import { specLabelsVi } from "../utils/specLabels";
import "./VehicleDetailPage.css";

const VehicleDetailPage = () => {
  const { id } = useParams();
  const [vehicle, setVehicle] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeTab, setActiveTab] = useState("specs");
  const [loading, setLoading] = useState(true);
  const [mediaList, setMediaList] = useState([]);
  const [selectedColor, setSelectedColor] = useState(null);
  const [currentImgUrl, setCurrentImgUrl] = useState(null);
  const [prevImgUrl, setPrevImgUrl] = useState(null);
  const [allVehicles, setAllVehicles] = useState([]);
  const [orderLink, setOrderLink] = useState("");
  const navigate = useNavigate();

  // States for pricing
  const [selectedPromos, setSelectedPromos] = useState({});
  const scrollRef = useRef(null);

  const handleScroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = window.innerWidth > 768 ? 600 : 300;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };
  const [selectedFeeCode, setSelectedFeeCode] = useState("FEE_HCM_HN");
  const [isVipColor, setIsVipColor] = useState(false);
  const [selectedCustomFees, setSelectedCustomFees] = useState({});
  const [dynamicPrices, setDynamicPrices] = useState({
    promotion_price: 0,
    final_price: 0,
    promo_amounts: {}
  });

  // Memoize static mappings
  const specLabels = specLabelsVi;
  const propMap = {
    specs: "specifications",
    engine: "engine",
    dimensions: "dimensions",
    features: "features",
  };

  const initPricingState = (variant, vehicleData = vehicle) => {
    const vData = vehicleData || vehicle;
    if (!vData) return;
    const initialPromos = {};
    if (vData.applicable_promotions) {
      vData.applicable_promotions.forEach(p => {
        initialPromos[p.promo_code] = p.promo_code === 'XEVF' || p.is_default === 1;
      });
    }
    setSelectedPromos(initialPromos);
    setIsVipColor(false);
    setSelectedCustomFees({});
  };

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoading(true);
        const minWaitPromise = new Promise(resolve => setTimeout(resolve, 1000));
        const data = await vehicleService.getVehicleById(id);
        if (data) {
          setVehicle(data);
          if (data.variants && data.variants.length > 0) {
            const variant = data.variants[0];
            setSelectedVariant(variant);
            initPricingState(variant, data);
          }
        }

        try {
          const mediaData = await vehicleService.getVehicleMediaByVehicleId(id);
          setMediaList(Array.isArray(mediaData) ? mediaData : []);
        } catch (mediaErr) {
          console.error("Error fetching vehicle media:", mediaErr);
        }
        await minWaitPromise;
      } catch (error) {
        console.error("Error fetching vehicle:", error);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchAllVehicles = async () => {
      try {
        const data = await vehicleService.getAllVehicles();
        setAllVehicles(data);
      } catch (error) {
        console.error("Error fetching all vehicles:", error);
      }
    };

    const fetchSettings = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || (window.location.hostname !== "localhost" ? "/api" : "http://localhost:5000/api")}/settings`
        );
        const data = await res.json();
        if (data && data.buy_guide_order_link) {
          setOrderLink(data.buy_guide_order_link);
        }
      } catch (error) {
        console.error("Error fetching settings:", error);
      }
    };

    fetchVehicle();
    fetchAllVehicles();
    fetchSettings();
  }, [id]);
  const handleVariantChange = (variant) => {
    setSelectedVariant(variant);
    initPricingState(variant);
  };



  useEffect(() => {
    if (selectedVariant && vehicle) {
      const publishedPrice = Number(selectedVariant.price?.published_price || 0);
      
      let totalOtherPromosAmount = 0;
      let promoAmounts = {};
      
      if (vehicle.applicable_promotions) {
        // 1. Tính các khuyến mãi thông thường (không phải XEVF)
        vehicle.applicable_promotions.forEach(p => {
          if (p.promo_code !== 'XEVF') {
            const amt = p.discount_type === 'amount'
              ? Number(p.discount_amount || 0)
              : publishedPrice * (Number(p.discount_percentage || 0) / 100);
            promoAmounts[p.promo_code] = amt;
            if (selectedPromos[p.promo_code]) {
              totalOtherPromosAmount += amt;
            }
          }
        });

        // 2. Tính riêng khuyến mãi XEVF (mặc định)
        const xevfPromo = vehicle.applicable_promotions.find(p => p.promo_code === 'XEVF');
        if (xevfPromo) {
          if (xevfPromo.discount_type === 'amount') {
            let amt = Number(xevfPromo.discount_amount || 0);
            if (selectedPromos['POLICE']) {
              amt = amt / 2;
            }
            promoAmounts['XEVF'] = amt;
          } else {
            let xevfPct = Number(xevfPromo.discount_percentage || 0);
            if (selectedPromos['POLICE']) {
              xevfPct = xevfPct / 2;
            }
            // XEVF amount = (Giá công bố - tổng tiền các khuyến mãi khác đã chọn) * tỷ lệ %
            const amt = (publishedPrice - totalOtherPromosAmount) * (xevfPct / 100);
            promoAmounts['XEVF'] = amt;
          }
        }
      }

      // 3. Tính tổng tiền khuyến mãi được áp dụng
      let totalPromotions = 0;
      if (vehicle.applicable_promotions) {
        vehicle.applicable_promotions.forEach(p => {
          if (p.promo_code === 'XEVF' || selectedPromos[p.promo_code]) {
            totalPromotions += (promoAmounts[p.promo_code] || 0);
          }
        });
      }

      // 4. Tính chi phí khác
      let totalFees = 0;
      if (vehicle.applicable_fees) {
        // Radio logic for HCM/HN vs Tỉnh
        const locFee = vehicle.applicable_fees.find(f => f.fee_code === selectedFeeCode);
        if (locFee) totalFees += Number(locFee.amount);

        // All other global fees are mandatory
        vehicle.applicable_fees.forEach(f => {
          if (f.fee_type === 'global' && f.fee_code !== 'FEE_HCM_HN' && f.fee_code !== 'FEE_PROV') {
            totalFees += Number(f.amount);
          }
        });

        if (isVipColor) {
          const vipFee = vehicle.applicable_fees.find(f => f.fee_code === 'FEE_VIP_COLOR');
          if (vipFee) totalFees += Number(vipFee.amount);
        }

        // Add custom per_vehicle fees
        vehicle.applicable_fees.forEach(f => {
          if (f.fee_type === 'per_vehicle' && f.fee_code !== 'FEE_VIP_COLOR' && selectedCustomFees[f.fee_code]) {
            totalFees += Number(f.amount);
          }
        });
      }

      const afterPromos = publishedPrice - totalPromotions;

      setDynamicPrices({
        promotion_price: Math.max(afterPromos, 0),
        final_price: Math.max(afterPromos + totalFees, 0),
        promo_amounts: promoAmounts
      });
    }
  }, [selectedVariant, selectedPromos, selectedFeeCode, isVipColor, selectedCustomFees, vehicle]);

  const getColorImage = (color, media) => {
    if (!color || !media || media.length === 0) return null;
    const images = media.filter((m) => m.media_type === "image");
    if (images.length === 0) return null;

    const viName = color.color_name_vi?.toLowerCase().trim();
    const enName = color.color_name_en?.toLowerCase().trim();
    const colorCode = color.color_code?.toLowerCase().trim();

    const normalize = (str) => str?.replace(/\s+/g, ' ').trim();

    let match = images.find((img) => {
      const label = normalize(img.label?.toLowerCase());
      const colName = normalize(img.color_name?.toLowerCase());
      
      const vName = normalize(viName);
      const eName = normalize(enName);
      const cCode = normalize(colorCode);

      const labelMatch =
        label &&
        (label === vName ||
          (eName && label === eName) ||
          (cCode && label === cCode));
      const colNameMatch =
        colName &&
        (colName === vName ||
          (eName && colName === eName) ||
          (cCode && colName === cCode));
      return labelMatch || colNameMatch;
    });

    if (match) return match;

    return match || null;
  };

  const availableColors =
    vehicle?.colors ?
      vehicle.colors.filter((color) => getColorImage(color, mediaList))
    : [];

  useEffect(() => {
    if (vehicle && availableColors.length > 0) {
      if (!selectedColor || !availableColors.find(c => c.color_id === selectedColor.color_id)) {
        setSelectedColor(availableColors[0]);
      }
    }
  }, [vehicle, availableColors, selectedColor]);

  const selectedImage = getColorImage(selectedColor, mediaList);

  useEffect(() => {
    const newUrl = selectedImage?.url || selectedImage?.media_url;
    if (newUrl && newUrl !== currentImgUrl) {
      setPrevImgUrl(currentImgUrl);
      setCurrentImgUrl(newUrl);
    }
  }, [selectedImage, currentImgUrl]);

  const handleColorSelection = (color) => {
    if (color.color_id !== selectedColor?.color_id) {
      setSelectedColor(color);
    }
  };

  const formatPrice = (price) => {
    if (!price) return "Liên hệ";
    return new Intl.NumberFormat("vi-VN", {
      style: "currency",
      currency: "VND",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const RacingLoader = () => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      let start = Date.now();
      const duration = 800;
      
      const animate = () => {
        const now = Date.now();
        const elapsed = now - start;
        const p = Math.min(100, Math.floor((elapsed / duration) * 100));
        setProgress(p);
        
        if (p < 100) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }, []);

    return (
      <div className="racing-loader-overlay">
        <div className="racing-loader-board">
          <div className="racing-loader-lights">
            <div className="light red"></div>
            <div className="light yellow"></div>
            <div className="light green"></div>
          </div>
          <div className="racing-loader-text">
            ĐANG TẢI DỮ LIỆU XE... 
            <span style={{ display: 'inline-block', width: '50px', textAlign: 'right' }}>
              {progress}%
            </span>
          </div>
          <div className="racing-loader-track">
            <img 
              src="https://shop.vinfastauto.com/on/demandware.static/-/Sites-app_vinfast_vn-Library/default/dw05aa09f9/images/VF2/TH14V/CE18.webp" 
              alt="VF 2 Racing" 
              className="racing-loader-car" 
            />
          </div>
        </div>
      </div>
    );
  };

  if (!vehicle && loading) return <RacingLoader />;
  if (!vehicle)
    return (
      <div style={{ padding: "100px", textAlign: "center" }}>
        Không tìm thấy thông tin xe!
      </div>
    );

  return (
    <div className="vehicle-detail-page">
      {loading && <RacingLoader />}
      <div className="vd-container">
        <div className="vd-header">
          <h1 className="vd-title">{vehicle.vehicle_name}</h1>
          <div className="vd-segment"><span>PHÂN KHÚC: {vehicle.segment}</span></div>
        </div>

        <div className="vd-layout">
          {/* Main Content (Left) */}
          <div className="vd-main">
            {/* Other Vehicles Section Moved Here */}
            {allVehicles.length > 0 && (
              <div className="vd-other-vehicles" style={{ marginTop: '0', marginBottom: '20px', background: 'var(--card-bg)', padding: '12px', borderRadius: '16px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', border: '1px solid var(--border-light)', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <button onClick={() => handleScroll('left')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-medium)', fontSize: '1.5rem', padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  &#10094;
                </button>

                <div className="vd-other-list" ref={scrollRef} style={{ padding: '10px 0', display: 'flex', gap: '15px', alignItems: 'center', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', flex: 1 }}>
                  {allVehicles
                    .filter((v) => String(v.vehicle_id) !== String(id))
                    .map((v) => (
                      <a
                        key={v.vehicle_id}
                        href={`/vehicle/${v.vehicle_id}`}
                        className="vd-other-card-3d"
                      >
                        {v.thumbnail && (
                          <img src={v.thumbnail} alt={v.vehicle_name} className="vd-other-img" style={{ width: '45px', height: '25px', objectFit: 'contain', margin: '0' }} />
                        )}
                        <span className="vd-other-name">{v.vehicle_name}</span>
                      </a>
                    ))}
                </div>

                <button onClick={() => handleScroll('right')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-medium)', fontSize: '1.5rem', padding: '0 10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  &#10095;
                </button>
              </div>
            )}

            <div className="vd-hero">
              <div className="vd-image-wrapper" style={{ position: 'relative', width: '100%', minHeight: '300px' }}>
                {!currentImgUrl && (
                  <div style={{ opacity: 0.5, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <i className="fas fa-car fa-5x"></i>
                  </div>
                )}
                {prevImgUrl && (
                  <img
                    key={`prev_${prevImgUrl}`}
                    src={prevImgUrl}
                    alt="Previous Car"
                    className="vd-image vd-image-fadeOut"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                  />
                )}
                {currentImgUrl && (
                  <img
                    key={`curr_${currentImgUrl}`}
                    src={currentImgUrl}
                    alt={vehicle?.vehicle_name || "Car"}
                    className="vd-image vd-image-fadeIn"
                    style={{ position: 'relative', zIndex: 1 }}
                  />
                )}
              </div>

              {availableColors.length > 0 && (
                <div className="vd-colors-wrapper">
                  <div className="vd-colors-title">
                    <span className="vd-color-name-vi">
                      {selectedColor?.color_name_vi}
                    </span>
                    {selectedColor?.color_name_en && (
                      <span className="vd-color-name-en">
                        {selectedColor.color_name_en}
                      </span>
                    )}
                  </div>
                  <div className="vd-colors-list">
                    {availableColors.map((color) => (
                      <div
                        key={color.color_id}
                        className={`vd-color-btn ${selectedColor?.color_id === color.color_id ? "active" : ""} ${color.is_vip === 1 ? "vip" : ""}`}
                        style={{
                          background:
                            color.is_vip === 1 ?
                              `linear-gradient(135deg, ${color.color_hex_code || color.hex_code || "#ccc"} 50%, ${color.color_hex_code_2 || color.color_hex_code || color.hex_code || "#ccc"} 50%)`
                            : color.color_hex_code || color.hex_code || "#ccc",
                        }}
                        title={
                          color.is_vip === 1 ?
                            `⭐ VIP - ${color.color_name_vi}`
                          : color.color_name_vi
                        }
                        onClick={() => handleColorSelection(color)}
                      >
                        {selectedColor?.color_id === color.color_id && (
                          <i className="fas fa-check"></i>
                        )}
                        {color.is_vip === 1 &&
                          selectedColor?.color_id !== color.color_id && (
                            <span className="vd-color-btn-vip-dot">⭐</span>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="vd-tabs">
              <div className="vd-tabs-nav">
                <button
                  className={`vd-tab-btn ${activeTab === "specs" ? "active" : ""}`}
                  onClick={() => setActiveTab("specs")}
                >
                  Khung Gầm
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "engine" ? "active" : ""}`}
                  onClick={() => setActiveTab("engine")}
                >
                  Động Cơ & Pin
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "exterior" ? "active" : ""}`}
                  onClick={() => setActiveTab("exterior")}
                >
                  Ngoại Thất
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "interior" ? "active" : ""}`}
                  onClick={() => setActiveTab("interior")}
                >
                  Nội Thất
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "safety" ? "active" : ""}`}
                  onClick={() => setActiveTab("safety")}
                >
                  An Toàn
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "adas" ? "active" : ""}`}
                  onClick={() => setActiveTab("adas")}
                >
                  ADAS
                </button>
                <button
                  className={`vd-tab-btn ${activeTab === "entertainment" ? "active" : ""}`}
                  onClick={() => setActiveTab("entertainment")}
                >
                  Giải Trí
                </button>
              </div>

              <div className="vd-tabs-content">
                {[
                  "specs",
                  "engine",
                  "exterior",
                  "interior",
                  "safety",
                  "adas",
                  "entertainment",
                ].includes(activeTab) && (
                  <div className="vd-specs-grid">
                    {(() => {
                      // Map activeTab to property in variant
                      const propMap = {
                        specs: "dimension",
                        engine: "engine", // Combine engine + battery manually later or just do engine first
                        exterior: "exterior",
                        interior: "interior",
                        safety: "safety",
                        adas: "adas",
                        entertainment: "entertainment",
                      };

                      const renderCategory = (catName) => {
                        const data = selectedVariant?.[catName];
                        if (!data) return null;
                        return Object.entries(data).map(([key, val]) => {
                          if (
                            key.endsWith("_id") ||
                            key === "created_at" ||
                            key === "vehicle_id"
                          )
                            return null;
                          if (val === null || val === undefined || val === "")
                            return null;
                          const label = specLabelsVi[key] || key;
                          return (
                            <div className="vd-spec-item" key={key}>
                              <span className="vd-spec-label">{label}</span>
                              <span className="vd-spec-value">{val}</span>
                            </div>
                          );
                        });
                      };

                      if (activeTab === "engine") {
                        return (
                          <>
                            {renderCategory("engine")}
                            {renderCategory("battery")}
                          </>
                        );
                      }
                      return renderCategory(propMap[activeTab]);
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sticky Sidebar (Right) */}
          <div className="vd-sidebar">
            <div className="vd-pricing-card">
              <div className="vd-pricing-header">Giá lăn bánh dự kiến</div>

              <div className="vd-variants-list">
                {vehicle.variants &&
                  vehicle.variants.map((variant) => (
                    <div
                      key={variant.variant_id}
                      className={`vd-variant-opt ${selectedVariant?.variant_id === variant.variant_id ? "active" : ""}`}
                      onClick={() => handleVariantChange(variant)}
                    >
                      <span className="vd-variant-name">
                        {variant.variant_name}
                      </span>
                      <span className="vd-variant-price">
                        {formatPrice(variant.price?.published_price)}
                      </span>
                    </div>
                  ))}
              </div>

              {selectedVariant && vehicle && (
                <>
                  {vehicle.applicable_promotions && vehicle.applicable_promotions.length > 0 && (
                    <div className="vd-fee-list">
                      <div className="vd-spec-label" style={{ marginBottom: "10px" }}>Khuyến mãi</div>
                      {[...vehicle.applicable_promotions].sort((a,b) => a.promo_code === 'POLICE' ? 1 : b.promo_code === 'POLICE' ? -1 : 0).map(promo => (
                        <div className="vd-fee-item" key={promo.promo_code}>
                          <label className="vd-fee-label">
                            <input
                              type="checkbox"
                              checked={promo.promo_code === 'XEVF' || !!selectedPromos[promo.promo_code]}
                              disabled={promo.promo_code === 'XEVF'}
                              onChange={(e) => setSelectedPromos({...selectedPromos, [promo.promo_code]: e.target.checked})}
                            />
                            {promo.promo_name}
                          </label>
                          <span className="vd-fee-amount vd-fee-discount">
                            - {formatPrice(dynamicPrices.promo_amounts ? dynamicPrices.promo_amounts[promo.promo_code] : 0)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="vd-fee-list">
                    <div className="vd-spec-label" style={{ marginBottom: "10px" }}>Chi phí & Phụ kiện</div>
                    
                    {/* 1. Registration Fees */}
                    {vehicle.applicable_fees?.filter(f => f.fee_code === 'FEE_HCM_HN' || f.fee_code === 'FEE_PROV').map(fee => (
                       <div className="vd-fee-item" key={fee.fee_code}>
                        <label className="vd-fee-label">
                          <input type="radio" checked={selectedFeeCode === fee.fee_code} onChange={() => setSelectedFeeCode(fee.fee_code)} />
                          {fee.fee_name}
                        </label>
                        <span className="vd-fee-amount">+ {formatPrice(fee.amount)}</span>
                      </div>
                    ))}

                    {/* 2. VIP Color */}
                    {vehicle.applicable_fees?.filter(f => f.fee_code === 'FEE_VIP_COLOR').map(fee => (
                       <div className="vd-fee-item" key={fee.fee_code}>
                        <label className="vd-fee-label">
                          <input type="checkbox" checked={isVipColor} onChange={e => setIsVipColor(e.target.checked)} />
                          {fee.fee_name}
                        </label>
                        <span className="vd-fee-amount">+ {formatPrice(fee.amount)}</span>
                      </div>
                    ))}

                    {/* 3. Custom Per Vehicle Fees */}
                    {vehicle.applicable_fees?.filter(f => f.fee_type === 'per_vehicle' && f.fee_code !== 'FEE_VIP_COLOR').map(fee => (
                       <div className="vd-fee-item" key={fee.fee_code}>
                        <label className="vd-fee-label">
                          <input type="checkbox" checked={!!selectedCustomFees[fee.fee_code]} onChange={e => setSelectedCustomFees({...selectedCustomFees, [fee.fee_code]: e.target.checked})} />
                          {fee.fee_name}
                        </label>
                        <span className="vd-fee-amount">+ {formatPrice(fee.amount)}</span>
                      </div>
                    ))}

                    {/* 4. Mandatory Global Fees */}
                    {vehicle.applicable_fees?.filter(f => f.fee_type === 'global' && f.fee_code !== 'FEE_HCM_HN' && f.fee_code !== 'FEE_PROV').map(fee => (
                       <div className="vd-fee-item" key={fee.fee_code}>
                        <label className="vd-fee-label" style={{ paddingLeft: '24px' }}>
                          {fee.fee_name}
                        </label>
                        <span className="vd-fee-amount">+ {formatPrice(fee.amount)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="vd-total-section">
                    <div className="vd-total-label">Giá Lăn Bánh Dự Kiến</div>
                    <div className="vd-total-value">
                      {formatPrice(dynamicPrices.final_price)}
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '15px', fontSize: '0.85rem', color: 'var(--text-light)', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.5' }}>
                    * Giá lăn bánh có thể thay đổi ở thời điểm bạn mua. Liên hệ với sale để được báo giá và chốt cọc kịp thời để được giá tốt nhất.
                  </div>

                  <div style={{ display: 'flex', gap: '10px', margin: '20px 0' }}>
                    <button 
                      className="btn btn-consult" 
                      onClick={() => navigate('/booking')}
                    >
                      LIÊN HỆ TƯ VẤN
                    </button>
                    <button 
                      className="btn btn-vinfast-order" 
                      onClick={() => {
                        if (orderLink) window.open(orderLink, '_blank');
                        navigate('/huong-dan-mua-xe');
                      }}
                    >
                      ĐẾN VINFAST ĐẶT XE
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetailPage;
